/**
 * Onglet Progression — suivi du poids (specs §6.6, §8.2 ; archi §4.6).
 *
 * Ordre de l'écran, dicté par les specs §4 (fréquence d'usage) :
 *
 *   1. **La pesée du jour, saisissable sur place.** Le cas d'usage n°2 des specs
 *      (« saisir le poids, une fois par jour, au réveil ») doit tenir en 1 à 2
 *      taps depuis l'accueil : un tap sur l'onglet, un tap dans le champ. Aucune
 *      feuille modale à ouvrir, aucun écran intermédiaire.
 *   2. **Les trois chiffres du §6.6** — brut, moyenne lissée 7 jours, tendance
 *      hebdomadaire.
 *   3. **Le graphique**, sur les quatre plages du §6.6.
 *   4. **L'objectif** et l'écart au rythme visé.
 *   5. **L'historique**, consultable et corrigeable.
 *
 * ⚠️ **Le rappel « matin à jeun » est une phrase, pas une mécanique.** Les specs
 * §6.6 demandent une saisie « le matin à jeun » ; la *notification* de rappel est
 * une des trois du §6.7 et relève de l'étape 9, côté serveur. Ici, on se contente
 * de le dire à l'endroit où l'on saisit — c'est ce qui rend les mesures
 * comparables entre elles, et donc la moyenne lissée interprétable.
 *
 * ⚠️ **Aucune mise à jour optimiste**, contrairement au journal. Ce n'est pas un
 * oubli : une pesée n'est pas sur le chemin critique des 15 secondes (specs §4),
 * et surtout écrire une valeur déplace la moyenne lissée de sept points et la
 * tendance. Prévoir cela côté client demanderait de dupliquer tout
 * `weightTrend.ts` pour gagner 200 ms sur un geste quotidien unique — le mauvais
 * marché. La réponse du serveur porte déjà l'état complet recalculé.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI » — ce qui a changé, et ce qui n'a pas bougé
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'habillage suit l'écran « Progression » du prototype : carte de pesée avec sa
 * capitale, rangée de trois chiffres, plages en pilules au-dessus du graphique,
 * carte d'objectif, historique en lignes filetées, note de pied. Les cinq blocs,
 * leur ordre, les quatre plages, les mutations et leur politique de cache sont
 * **inchangés**.
 *
 * ⚠️ **`WeightChart` n'a pas de jetons à lui.** Ses quatre teintes viennent de
 * la palette de la refonte — `--ink-soft` pour le brut, `--brand` pour la
 * lissée, `--ink` pour l'objectif tireté, `--line` pour la grille. Le prototype
 * n'en dessine qu'un croquis à deux séries : il n'y avait rien à transcrire que
 * la palette ne dise déjà, et une palette parallèle pour un seul écran aurait
 * été une seconde source de vérité.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';

import type {
  WeightGoalInput,
  WeightRange,
  WeightResponse,
  WeightSummaryDTO,
} from '../../../../shared/types';
import { readError } from '../../api/client';
import {
  createWeightGoal,
  deleteWeight,
  deleteWeightGoal,
  fetchWeight,
  saveWeight,
  WEIGHT_GOALS_KEY,
  weightKey,
} from '../../api/weight';
import { Button, Card, TextField } from '../../components/nourri';
import { ScreenTitle } from '../../components/ui/ScreenHeader';
import { SectionTabs, type SectionTab } from '../../components/ui/SectionTabs';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { useConfirm } from '../../hooks/useConfirm';
import { newClientId } from '../../lib/dates';
import { toNumber } from '../../lib/formFields';
import { ActivitySection } from './ActivitySection';
import { NutritionSection } from './NutritionSection';
import { TrainingSection } from './TrainingSection';
import { WeightChart } from './WeightChart';
import { WeightGoalPanel } from './WeightGoalPanel';
import { WeightHistory } from './WeightHistory';
import { formatKg, formatSigned, MAX_KG, MIN_KG } from './weightFields';

/** Les quatre plages du §6.6, dans l'ordre où on les parcourt. */
const RANGES: readonly { value: WeightRange; label: string }[] = [
  { value: '30d', label: '30 j' },
  { value: '90d', label: '90 j' },
  { value: '1y', label: '1 an' },
  { value: 'all', label: 'Tout' },
];

/**
 * Un des trois chiffres de tête — prototype `.stat-row > div`.
 *
 * 🔴 **Libellé au-dessus de la valeur, jamais à côté.** Exactement la correction
 * apportée à `MacroChip` à l'étape 7 : trois colonnes se partagent 393 px, et
 * « Tendance » + « −0,42 kg/sem. » côte à côte déborde de sa colonne pour
 * chevaucher la voisine. Les valeurs de cet écran sont même **plus larges** que
 * celles du journal — un signe, une virgule décimale et une unité composée.
 * Empiler coûte une ligne et rend la rangée régulière quelles que soient les
 * valeurs. Le prototype fait le même choix.
 *
 * ⚠️ `nowrap` sur la valeur (posé par `.n-stat__val`) : c'est elle qui ne doit
 * jamais se couper en deux (« −0, » puis « 42 »).
 */
function Figure({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div>
      <div className="n-stat__lbl">{label}</div>
      <div className="n-stat__val tnum">
        {value}
        {unit !== undefined && <span className="n-stat__unit">{unit}</span>}
      </div>
    </div>
  );
}

/** Les trois chiffres du §6.6 : brut, moyenne lissée, tendance. */
function SummaryCard({ summary }: { summary: WeightSummaryDTO }) {
  return (
    <Card>
      <div className="n-stat-row">
        <Figure
          label="Poids brut"
          value={summary.latest === null ? '—' : formatKg(summary.latest.kg)}
          unit={summary.latest === null ? undefined : 'kg'}
        />
        <Figure
          label="Moyenne 7 j"
          value={summary.smoothed === null ? '—' : formatKg(summary.smoothed)}
          unit={summary.smoothed === null ? undefined : 'kg'}
        />
        <Figure
          label="Tendance"
          value={summary.trendKgWeek === null ? '—' : formatSigned(summary.trendKgWeek)}
          unit={summary.trendKgWeek === null ? undefined : 'kg/sem.'}
        />
      </div>

      {summary.trendKgWeek === null ? (
        <p className="n-hint" style={{ marginTop: 14 }}>
          La tendance demande au moins trois pesées étalées sur une semaine : en dessous, la
          pente mesurée ne dirait que le bruit du quotidien.
        </p>
      ) : (
        <p className="n-hint" style={{ marginTop: 14 }}>
          Tendance établie sur les {summary.trendWindowDays} derniers jours —{' '}
          {summary.trendSampleCount} pesées.
        </p>
      )}
    </Card>
  );
}

/** Saisie de la pesée du jour — le geste quotidien, sans feuille intermédiaire. */
function TodayCard({
  data,
  saving,
  confirming,
  onSave,
}: {
  data: WeightResponse;
  saving: boolean;
  /** Micro-animation de validation — cf. `useConfirm` et `lib/haptics.ts`. */
  confirming: boolean;
  onSave: (value: number) => void;
}) {
  const [draft, setDraft] = useState('');

  const stored = data.todayEntry;
  // Le champ suit la valeur enregistrée tant qu'on n'a rien tapé : rouvrir
  // l'onglet doit montrer la pesée du jour, pas un champ vide.
  const shown = draft === '' && stored !== null ? formatKg(stored.kg) : draft;
  const value = toNumber(shown);
  const valid = value >= MIN_KG && value <= MAX_KG;
  const changed = stored === null || Math.abs(value - stored.kg) > 0.001;

  return (
    <Card>
      {/* Prototype `.eyebrow` — la capitale qui nomme le bloc. */}
      <div className="n-eyebrow">
        {stored === null ? 'Pesée du jour' : 'Pesée du jour (enregistrée)'}
      </div>

      <div className="n-field-row" style={{ marginTop: 12 }}>
        <TextField
          aria-label={stored === null ? 'Pesée du jour' : 'Pesée du jour (enregistrée)'}
          unit="kg"
          numeric
          inputMode="decimal"
          value={shown}
          onChange={(event) => {
            // Virgule décimale : c'est ce que produit le pavé numérique
            // français. Comportement repris tel quel de `NumberField`.
            setDraft(event.target.value.replace(',', '.'));
          }}
          /*
            ⚠️ Placeholder **vide**, et non un « 0 » d'indication. Sur un champ
            de macro, un zéro grisé est une valeur plausible et sert de repère ;
            sur une pesée, c'est la seule valeur que le champ n'acceptera jamais
            — et elle se lit comme un poids déjà saisi.
          */
          placeholder=""
          autoComplete="off"
        />
        {/*
          ⚠️ Ici l'animation se joue bien **sur le bouton**, contrairement au
          journal où elle est portée par le bandeau : cet écran n'ouvre aucune
          feuille modale, le bouton reste donc sous les yeux après la validation.
        */}
        <Button
          variant="primary"
          shape="rounded"
          className={confirming ? 'is-confirming' : undefined}
          disabled={!valid || !changed || saving}
          onClick={() => {
            onSave(value);
            setDraft('');
          }}
        >
          {stored === null ? 'Saisir' : 'Corriger'}
        </Button>
      </div>

      {/* Le rappel du §6.6 — une phrase, pas une mécanique. La notification
          correspondante est une des trois du §6.7. */}
      <p className="n-hint" style={{ marginTop: 12 }}>
        Toujours dans les mêmes conditions : le matin, à jeun, avant de boire. C’est ce qui rend
        les pesées comparables entre elles.
      </p>
    </Card>
  );
}

/**
 * Le suivi du poids — ce que cet écran a toujours été.
 *
 * ⚠️ **Extrait tel quel** au moment où Progression a pris un second sous-onglet
 * (V2 étape 8). Rien n'a changé de son contenu, de son ordre, de ses mutations
 * ni de sa politique de cache : seuls le `<section>` et le titre sont remontés
 * dans `ProgressScreen`, qui les partage désormais avec l'onglet Activité.
 * C'est la discipline de l'extraction d'`ExerciseFacts` à l'étape 6 —
 * « extrait, pas recopié ».
 */
function WeightSection() {
  const queryClient = useQueryClient();
  const [range, setRange] = useState<WeightRange>('90d');

  /**
   * 🔴 **`placeholderData` : changer de plage ne vide plus l'écran.**
   *
   * Relevé à l'usage. Chaque plage a sa propre clé de cache, donc passer de
   * « 90 j » à « 30 j » partait sur une requête neuve, `weight.data` devenait
   * `undefined`, et **tout l'écran** — pesée du jour, trois chiffres, objectif,
   * historique — disparaissait derrière « Chargement… » le temps d'un
   * aller-retour. Or une seule chose dépend de la plage : ce qui est dessiné.
   *
   * En gardant les données précédentes, l'écran ne bouge pas ; seul le bloc du
   * graphique se signale comme en cours de mise à jour (`isPlaceholderData`).
   *
   * ⚠️ La fonction reçoit les données de la **clé précédente**, ce qui est
   * exactement l'effet recherché — et elle rend `undefined` au tout premier
   * chargement, où il n'y a rien à garder et où l'écran doit bien afficher son
   * état de chargement.
   */
  const weight = useQuery({
    queryKey: weightKey(range),
    queryFn: () => fetchWeight(range),
    placeholderData: (previous) => previous,
  });

  /**
   * Toute mutation renvoie l'état complet : on le pose directement dans le cache
   * de la plage courante plutôt que d'invalider et de refaire un aller-retour.
   *
   * ⚠️ Les **autres** plages sont invalidées, elles : corriger une pesée du mois
   * dernier change aussi ce qu'affichent « 1 an » et « Tout ». Les laisser en
   * cache montrerait deux vérités différentes selon le bouton pressé.
   */
  const applyResponse = (response: WeightResponse) => {
    queryClient.setQueryData(weightKey(range), response);
    for (const other of RANGES) {
      if (other.value !== range) {
        void queryClient.invalidateQueries({ queryKey: weightKey(other.value) });
      }
    }
    void queryClient.invalidateQueries({ queryKey: WEIGHT_GOALS_KEY });
  };

  /*
   * Retour de validation (specs §8.1).
   *
   * ⚠️ **Ici, au succès — et non au geste comme au journal.** La différence
   * tient à ce que l'écran fait : le journal écrit en optimiste, donc l'écran a
   * déjà changé quand le doigt se lève, et la confirmation accompagne ce
   * changement. Une pesée, elle, n'est **pas** optimiste (décision de
   * l'étape 8 : prévoir l'état demanderait de dupliquer tout `weightTrend.ts`).
   * Rien ne bouge tant que le serveur n'a pas répondu ; confirmer avant serait
   * annoncer un enregistrement qui peut encore échouer.
   */
  const { confirming, confirm } = useConfirm();

  const save = useMutation({
    mutationFn: ({ date, kg }: { date: string; kg: number }) => saveWeight(date, { kg }, range),
    onSuccess: (response) => {
      applyResponse(response);
      confirm();
    },
  });

  const remove = useMutation({
    mutationFn: (date: string) => deleteWeight(date, range),
    onSuccess: applyResponse,
  });

  /**
   * Nouvel objectif.
   *
   * ⚠️ **`clientId` tiré au geste**, dans `onSubmit`, et non ici : il doit être
   * *stable* pour un même geste et *neuf* d'un geste à l'autre (V2 §2.2). Le
   * tirer dans `mutationFn` en fabriquerait un nouveau à chaque réémission —
   * donc exactement le doublon que la colonne existe pour empêcher. Même
   * discipline que `addEntry` dans `useDay`.
   */
  const setGoal = useMutation({
    mutationFn: (body: WeightGoalInput) => createWeightGoal(body, range),
    onSuccess: applyResponse,
  });

  const dropGoal = useMutation({
    mutationFn: (id: number) => deleteWeightGoal(id, range),
    onSuccess: applyResponse,
  });

  const busy = save.isPending || remove.isPending || setGoal.isPending || dropGoal.isPending;
  /*
   * 🔴 **Lecture et écriture séparées** (V2 §3.5). Les deux étaient fusionnées
   * dans une seule variable : hors ligne, l'échec de rafraîchissement du
   * graphique produisait donc un bandeau rouge par-dessus des pesées
   * parfaitement affichées. `readError` masque ce cas ; une mutation en échec,
   * elle, reste annoncée — rien ne la rejouera avant la file de l'étape 3.
   */
  const mutationError = save.error ?? remove.error ?? setGoal.error ?? dropGoal.error ?? null;
  const lecture = readError(weight);

  return (
    <>
      {mutationError && <ErrorNote>{mutationError.message}</ErrorNote>}
      <ReadNote error={lecture} quoi="l’historique de poids" />
      {weight.data === undefined && lecture === null && <Hint>Chargement…</Hint>}

      {weight.data && (
        <>
          <TodayCard
            data={weight.data}
            saving={busy}
            confirming={confirming}
            onSave={(value) => {
              save.mutate({ date: weight.data.today, kg: value });
            }}
          />

          <SummaryCard summary={weight.data.summary} />

          <Card>
            {/*
              Une seule rangée de plages, **au-dessus** de tout ce qu'elle porte :
              graphique et historique changent ensemble, donc les deux racontent
              toujours la même période. Des boutons par bloc auraient permis de
              lire un graphique sur 30 jours au-dessus d'une liste sur un an.

              ⚠️ `variant="selected"` pour la plage active — la pastille foncée
              du prototype (`.period-btn.active`). C'est le même couple que les
              onglets de la Bibliothèque : le système n'a qu'une façon de dire
              « celui-ci », et elle ne dépend pas de la couleur de marque.
            */}
            <div className="n-period-row" style={{ marginBottom: 14 }}>
              {RANGES.map((option) => {
                const active = option.value === range;
                return (
                  <Button
                    key={option.value}
                    variant={active ? 'selected' : 'secondary'}
                    aria-pressed={active}
                    onClick={() => {
                      setRange(option.value);
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>

            {/*
              ⚠️ **Le seul endroit qui signale un changement de plage.** Une
              opacité sur le graphique, et rien d'autre : le reste de l'écran ne
              dépend pas de la plage et n'a aucune raison de clignoter. Sur un
              réseau normal l'aller-retour est trop court pour qu'on le voie —
              c'est voulu, ce n'est pas une animation, c'est un aveu d'attente
              quand l'attente existe.
            */}
            <div
              className="n-chart"
              style={weight.isPlaceholderData ? { opacity: 0.45 } : undefined}
              aria-busy={weight.isPlaceholderData}
            >
              <WeightChart
                points={weight.data.points}
                goalKg={weight.data.goal?.targetKg ?? null}
              />
            </div>
          </Card>

          <WeightGoalPanel
            goal={weight.data.goal}
            summary={weight.data.summary}
            saving={busy}
            onSubmit={(targetKg, rateKgWeek) => {
              // Un identifiant neuf par geste : reconduire le même objectif
              // demain écrira bien une seconde ligne (V2 §2.2).
              setGoal.mutate({ targetKg, rateKgWeek, clientId: newClientId() });
            }}
            onRemove={(id) => {
              dropGoal.mutate(id);
            }}
          />

          <WeightHistory
            points={weight.data.points}
            today={weight.data.today}
            saving={busy}
            onSave={(date, value) => {
              save.mutate({ date, kg: value });
            }}
            onDelete={(date) => {
              remove.mutate(date);
            }}
          />

          {weight.data.totalEntries > weight.data.points.length && (
            <p className="n-foot-note">
              {weight.data.totalEntries} pesées au total — « Tout » affiche l’historique complet.
            </p>
          )}
        </>
      )}
    </>
  );
}

/* ==========================================================================
 * L'onglet
 * ======================================================================= */

/**
 * Les deux sous-onglets de Progression (specs §8.2 : « Poids, tendance,
 * graphiques » + « Statistiques, activités Coros »).
 *
 * ⚠️ **Le poids reste la section par défaut**, servie par `/progression` nu.
 * C'est le cas d'usage n°5 des specs (« consulter la tendance de poids,
 * hebdomadaire ») contre une consultation d'activité encore plus occasionnelle,
 * et surtout c'est l'adresse que la barre d'onglets ouvre : la faire tomber sur
 * un écran neuf aurait déplacé un geste quotidien pour un ajout.
 *
 * ⚠️ Deux libellés courts — il reste largement la place pour le troisième que
 * l'étape 9 (statistiques) ajoutera, sans rejouer la mesure de l'étape 4.
 */
const SECTIONS: readonly SectionTab[] = [
  { section: null, label: 'Poids' },
  { section: 'nutrition', label: 'Nutrition' },
  { section: 'muscu', label: 'Muscu' },
  { section: 'activite', label: 'Activité' },
];

export function ProgressScreen() {
  const { section } = useParams<{ section?: string }>();

  /*
   * 🔴 **La section est une ROUTE, jamais un `useState`** — la règle du projet,
   * et elle a deux moitiés que l'étape 7 a fini de payer. Un état local
   * disparaîtrait au changement d'onglet (« au moment précis où il faudrait s'en
   * souvenir ») **et** survivrait à un appui sur l'onglet actif (« au moment
   * précis où il faudrait l'oublier », défaut du détail de routine, 31/08).
   * En route, on gagne la mémoire d'onglet, le retour par glissement natif et le
   * montage paresseux de chaque section.
   *
   * ⚠️ **Une section inconnue retombe sur le poids**, elle n'affiche pas un
   * écran vide : `/progression/nimportequoi` montre la page par défaut. Même
   * normalisation que les Réglages et la Musculation.
   */
  const current =
    section === 'activite' || section === 'nutrition' || section === 'muscu' ? section : null;

  return (
    <section className="n-page">
      <ScreenTitle>Progression</ScreenTitle>

      <SectionTabs
        base="/progression"
        tabs={SECTIONS}
        current={current}
        ariaLabel="Sections de Progression"
      />

      {current === 'activite' && <ActivitySection />}
      {current === 'nutrition' && <NutritionSection />}
      {current === 'muscu' && <TrainingSection />}
      {current === null && <WeightSection />}
    </section>
  );
}
